import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useAuth } from './AuthContext';

const CartContext = createContext();
const GUEST_CART_STORAGE_KEY = 'poupix_guest_cart_v1';
const SERVER_CART_STORAGE_KEY = 'poupix_server_cart_v1';

const canUseBrowserStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage);

const normalizeCartItem = (item) => {
  if (!item || typeof item !== 'object' || !item.product || item.product.id == null) {
    return null;
  }

  const quantity = Number.parseInt(item.quantity, 10);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return null;
  }

  return {
    product: item.product,
    quantity,
    priceInfo: {
      price: Number(item.priceInfo?.price) || 0,
      supermarket: item.priceInfo?.supermarket || null,
      supermarket_id: item.priceInfo?.supermarket_id || null,
    },
  };
};

const readStoredCart = (storageKey) => {
  if (!canUseBrowserStorage()) return [];

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    const items = Array.isArray(parsed) ? parsed : parsed?.items;
    if (!Array.isArray(items)) return [];

    return items.map(normalizeCartItem).filter(Boolean);
  } catch (_) {
    return [];
  }
};

const persistStoredCart = (storageKey, items) => {
  if (!canUseBrowserStorage()) return;

  const normalizedItems = items.map(normalizeCartItem).filter(Boolean);
  if (normalizedItems.length === 0) {
    window.localStorage.removeItem(storageKey);
    return;
  }

  window.localStorage.setItem(storageKey, JSON.stringify({ items: normalizedItems }));
};

const clearStoredCart = (storageKey) => {
  if (!canUseBrowserStorage()) return;
  window.localStorage.removeItem(storageKey);
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const cartKey = (item) => `${item.product.id}::${item.priceInfo.supermarket_id || 'none'}`;

export const CartProvider = ({ children }) => {
  const { isAuthenticated, authHeaders, token } = useAuth();
  const [cartItems, setCartItems] = useState(() => (
    readStoredCart(token ? SERVER_CART_STORAGE_KEY : GUEST_CART_STORAGE_KEY)
  ));
  const didInitAuthCartRef = useRef(false);
  const [lastAdded, setLastAdded] = useState(null);
  const lastAddedTimerRef = useRef(null);
  const [cartSource, setCartSource] = useState(() => (token ? 'server' : 'guest'));

  const syncServerItem = useCallback(async (productId, quantity, supermarketId = null) => {
    if (!isAuthenticated) return;
    await axios.put(
      `${API_BASE_URL}/cart/items/${productId}`,
      { quantity, supermarket_id: supermarketId },
      { headers: authHeaders }
    );
  }, [isAuthenticated, authHeaders]);

  const loadServerCart = useCallback(async () => {
    if (!isAuthenticated) return;
    const res = await axios.get(`${API_BASE_URL}/cart`, { headers: authHeaders });
    const items = (res.data.items || [])
      .filter((item) => item.product)
      .map((item) => ({
        product: item.product,
        quantity: item.quantity,
        priceInfo: {
          price: item.selected_price || 0,
          supermarket: item.supermarket,
          supermarket_id: item.supermarket_id,
        },
      }));

    setCartItems(items);
    setCartSource('server');
  }, [isAuthenticated, authHeaders]);

  useEffect(() => {
    if (token) {
      persistStoredCart(SERVER_CART_STORAGE_KEY, cartItems);
      return;
    }

    if (cartSource === 'guest') {
      persistStoredCart(GUEST_CART_STORAGE_KEY, cartItems);
    }
  }, [cartItems, cartSource, token]);

  useEffect(() => {
    if (!isAuthenticated) {
      didInitAuthCartRef.current = false;
      return;
    }

    if (didInitAuthCartRef.current) return;
    didInitAuthCartRef.current = true;

    const guestItemsSnapshot = [...cartItems];
    const shouldSyncGuestCart = cartSource === 'guest' && guestItemsSnapshot.length > 0;

    const syncThenLoad = async () => {
      if (shouldSyncGuestCart) {
        await axios.post(
          `${API_BASE_URL}/cart/sync`,
          {
            items: guestItemsSnapshot.map((item) => ({
              product_id: item.product.id,
              quantity: item.quantity,
              supermarket_id: item.priceInfo?.supermarket_id || null,
            })),
          },
          { headers: authHeaders }
        );
        clearStoredCart(GUEST_CART_STORAGE_KEY);
      }
      await loadServerCart();
    };

    syncThenLoad().catch((error) => {
      console.error('Failed to load server cart:', error);
    });
  }, [isAuthenticated, cartItems, cartSource, loadServerCart, authHeaders]);

  const addToCart = useCallback(async (product, priceInfo, quantity = 1) => {
    const incoming = {
      product,
      priceInfo,
      quantity,
    };

    setCartSource(token ? 'server' : 'guest');

    setCartItems((prev) => {
      const existingIndex = prev.findIndex((item) => cartKey(item) === cartKey(incoming));
      if (existingIndex >= 0) {
        return prev.map((item, idx) => (
          idx === existingIndex ? { ...item, quantity: item.quantity + quantity } : item
        ));
      }
      return [...prev, incoming];
    });

    setLastAdded({ id: Date.now(), product, quantity });
    if (lastAddedTimerRef.current) clearTimeout(lastAddedTimerRef.current);
    lastAddedTimerRef.current = setTimeout(() => setLastAdded(null), 4200);

    const supermarketId = priceInfo?.supermarket_id || null;
    await syncServerItem(product.id, quantity, supermarketId);
  }, [syncServerItem, token]);

  const removeFromCart = useCallback(async (productId) => {
    setCartSource(token ? 'server' : 'guest');
    setCartItems((prev) => prev.filter((item) => item.product.id !== productId));

    if (isAuthenticated) {
      await axios.delete(`${API_BASE_URL}/cart/items/${productId}`, { headers: authHeaders });
    }
  }, [isAuthenticated, authHeaders, token]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (quantity <= 0) {
      await removeFromCart(productId);
      return;
    }

    let supermarketId = null;
    setCartSource(token ? 'server' : 'guest');
    setCartItems((prev) => prev.map((item) => {
      if (item.product.id === productId) {
        supermarketId = item.priceInfo?.supermarket_id || null;
        return { ...item, quantity };
      }
      return item;
    }));

    await syncServerItem(productId, quantity, supermarketId);
  }, [removeFromCart, syncServerItem, token]);

  const getCheapestSupermarket = () => {
    const bySuper = {};
    cartItems.forEach((item) => {
      const supermarket = item.priceInfo.supermarket || 'Unknown';
      if (!bySuper[supermarket]) bySuper[supermarket] = [];
      bySuper[supermarket].push(item);
    });

    for (const [supermarketName, items] of Object.entries(bySuper)) {
      if (items.length === cartItems.length) {
        return supermarketName;
      }
    }
    return null;
  };

  const getGroupedBySuper = () => {
    const grouped = {};
    cartItems.forEach((item) => {
      const supermarket = item.priceInfo.supermarket || 'Unknown';
      if (!grouped[supermarket]) grouped[supermarket] = [];
      grouped[supermarket].push(item);
    });
    return grouped;
  };

  const getTotalPrice = (items = cartItems) => {
    return items.reduce((total, item) => total + ((item.priceInfo.price || 0) * item.quantity), 0);
  };

  const clearCart = useCallback(async () => {
    const productIds = cartItems.map((item) => item.product.id);
    setCartSource(token ? 'server' : 'guest');
    setCartItems([]);

    if (isAuthenticated) {
      await Promise.all(
        productIds.map((productId) =>
          axios.delete(`${API_BASE_URL}/cart/items/${productId}`, { headers: authHeaders })
        )
      );
    }
  }, [cartItems, isAuthenticated, authHeaders, token]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCheapestSupermarket,
        getGroupedBySuper,
        getTotalPrice,
        lastAdded,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
